import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CurrentPageNumberWrapper, PaginationWrapper } from "./Styled";
import { PaginationBarConfig } from "../../../configs/productOrder";
import FirstPage from "../../svg/PaginationBar/FirstPage";
import PreviousPage from "../../svg/PaginationBar/PreviousPage";
import NextPage from "../../svg/PaginationBar/NextPage";
import LastPage from "../../svg/PaginationBar/LastPage";
import DropdownMenu from "../DropdownMenu.tsx";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import { color } from "../../styles";
import { Box } from "@mui/material";

interface PageSelectorProps {
    totalItems: number;
    pageSize: number;
    setPageSize?: Function;
    fetchItems: Function;
}

export const DEFAULT_PAGE_SIZE = 10;

const PageSizeSelector = ({
    pageSize,
    totalItems,
    setPageSize,
    fetchItems,
}: PageSelectorProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const menuList = [5, 10, 15, 25, 50, 100, 150].map((size) => ({
        text: size,
    }));

    const onClickSelector = (item: any) => {
        // Update URL with new page size
        const params = new URLSearchParams(location.search);
        
        // Preserve the current tab parameter
        const currentTab = params.get('tab');
        
        params.set('page_size', item.text.toString());
        params.set('page', '1'); // Reset to page 1 when changing page size
        
        // Ensure tab parameter is preserved
        if (currentTab) {
            params.set('tab', currentTab);
        }
        
        if (setPageSize) {
            setPageSize(item.text);
        }
        
        const newURL = `${location.pathname}?${params.toString()}`;
        navigate(newURL, { replace: true });
        
        fetchItems({ page_size: item.text, page: 1 });
    };

    return (
        <CurrentPageNumberWrapper data-testid="page-size-selector">
            {PaginationBarConfig.text}
            <DropdownMenu
                menuList={menuList}
                menuText={pageSize.toString()}
                handleOnclickItem={onClickSelector}
                style={{
                    borderRadius: "8px",
                }}
            />
            {PaginationBarConfig.page} {totalItems}
        </CurrentPageNumberWrapper>
    );
};

export interface PaginationData {
    pageSize: number;
    pageNumber: number;
    totalPages: number;
}

interface PaginationBarProps {
    pageSize: number;
    setPageSize?: Function;
    totalItems: number;
    fetchItems: Function;
    currentPage: number;
    setCurrentPage: Function;
}

const PaginationBar = ({
    pageSize,
    setPageSize,
    totalItems,
    fetchItems,
    currentPage,
    setCurrentPage,
}: PaginationBarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const totalPages = Math.ceil(totalItems / pageSize);

    const handlePageChange = (event: any, page: number) => {
        setCurrentPage(page);
        
        // Update URL with new page
        const params = new URLSearchParams(location.search);
        
        // Preserve the current tab parameter
        const currentTab = params.get('tab');
        
        params.set('page', page.toString());
        
        // Ensure tab parameter is preserved
        if (currentTab) {
            params.set('tab', currentTab);
        }
        
        const newURL = `${location.pathname}?${params.toString()}`;
        navigate(newURL, { replace: true });
        
        fetchItems({ page });
    };

    return (
        <PaginationWrapper>
            <PageSizeSelector
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalItems={totalItems}
                fetchItems={fetchItems}
            />
            <Box data-testid="pagination-bar">
                <Pagination
                    page={currentPage}
                    count={totalPages}
                    showFirstButton
                    showLastButton
                    onChange={handlePageChange}
                    renderItem={(item) => (
                        <PaginationItem
                            slots={{
                                previous: PreviousPage,
                                next: NextPage,
                                first: FirstPage,
                                last: LastPage,
                            }}
                            {...item}
                            sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                "&.Mui-selected": {
                                    backgroundColor: color.status.INFO_LIGHT,
                                },
                                ":hover": {
                                    backgroundColor: color.status.INFO_LIGHT,
                                },
                                "&.Mui-active": {
                                    backgroundColor: color.status.INFO_LIGHT,
                                },
                            }}
                        />
                    )}
                />
            </Box>
        </PaginationWrapper>
    );
};

export default PaginationBar;
